/**
 * Formatting utilities for the admin dashboard
 */

/**
 * Format a number with comma separators
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }
  const num = Number(value);
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format a value as USD currency with comma separators
 * @param {number|string} value - The amount to format
 * @param {boolean} showCents - Whether to show cents (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, showCents = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }
  const num = Number(value);
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });
};

/**
 * Format a value as a percentage
 * @param {number|string} value - The percentage value (e.g., 15 for 15%)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 1) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }
  const num = Number(value);
  return `${num.toFixed(decimals)}%`;
};

/**
 * Calculate and format the delta between current and previous values
 * @param {number} current - Current period value
 * @param {number} previous - Previous period value
 * @param {string} format - 'number', 'currency', or 'percent'
 * @returns {object} Delta information { change, percentChange, isPositive, formatted }
 */
export const calculateDelta = (current, previous, format = 'number') => {
  if (
    current === null || current === undefined || isNaN(current) ||
    previous === null || previous === undefined || isNaN(previous)
  ) {
    return { change: 0, percentChange: 0, isPositive: null, formatted: '—' };
  }

  const curr = Number(current);
  const prev = Number(previous);
  const change = curr - prev;
  const percentChange = prev !== 0 ? ((change / prev) * 100) : (curr !== 0 ? 100 : 0);
  const isPositive = change > 0;
  const isNegative = change < 0;
  const isZero = change === 0;

  let formattedChange;
  switch (format) {
    case 'currency':
      formattedChange = formatCurrency(Math.abs(change));
      break;
    case 'percent':
      formattedChange = formatPercent(Math.abs(change));
      break;
    default:
      formattedChange = formatNumber(Math.abs(change));
  }

  const arrow = isPositive ? '↑' : isNegative ? '↓' : '→';
  const sign = isPositive ? '+' : isNegative ? '-' : '';
  const formatted = isZero
    ? 'No change'
    : `${arrow} ${sign}${formattedChange} (${sign}${Math.abs(percentChange).toFixed(1)}%)`;

  return {
    change,
    percentChange,
    isPositive: isPositive ? true : isNegative ? false : null,
    formatted,
    arrow,
  };
};

/**
 * Format days with appropriate suffix
 * @param {number} days - Number of days
 * @param {number} decimals - Number of decimal places (default: 0 = rounded)
 * @returns {string} Formatted string like "3 days" or "3.15 days"
 */
export const formatDays = (days, decimals = 0) => {
  if (days === null || days === undefined || isNaN(days)) {
    return '—';
  }
  const num = Number(days);
  const formatted = decimals > 0 ? num.toFixed(decimals) : Math.round(num);
  return `${formatted} ${num === 1 ? 'day' : 'days'}`;
};
